import $ from 'jquery';

/**
 * Modal Accessibility Utility
 * Provides Escape key handling and focus trapping for custom modals/popups.
 *
 * Usage:
 *   import { bindEscapeClose, trapFocus, releaseFocus } from '../common/js/modalA11y';
 *
 *   // When modal opens:
 *   trapFocus($modalElement);
 *
 *   // When modal closes:
 *   releaseFocus();
 *
 *   // Bind Escape key to close a modal:
 *   bindEscapeClose(isVisibleFn, closeFn);
 */

const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

let _activeTrapHandler = null;
let _triggerElement = null;

/**
 * Bind Escape key to close a modal.
 * @param {Function} isVisibleFn - Returns true if the modal is currently visible
 * @param {Function} closeFn - Called to close the modal
 */
function bindEscapeClose(isVisibleFn, closeFn) {
    $(document).on('keydown.modalA11y', function (e) {
        if (e.key === 'Escape' && isVisibleFn()) {
            e.preventDefault();
            e.stopPropagation();
            closeFn();
        }
    });
}

/**
 * Trap keyboard focus within a modal element.
 * Also stores the currently focused element to restore later.
 * @param {jQuery} $modal - The modal/popup container element
 */
function trapFocus($modal) {
    _triggerElement = document.activeElement;

    // Small delay to let DOM update before focusing
    setTimeout(function () {
        const $focusable = $modal.find(FOCUSABLE_SELECTORS).filter(':visible');
        if ($focusable.length) {
            $focusable.first().trigger('focus');
        }
    }, 100);

    _activeTrapHandler = function (e) {
        if (e.key !== 'Tab') {return;}

        const $focusable = $modal.find(FOCUSABLE_SELECTORS).filter(':visible');
        if (!$focusable.length) {return;}

        const $first = $focusable.first();
        const $last = $focusable.last();

        if (e.shiftKey) {
            // Shift+Tab: if on first focusable, wrap to last
            if (document.activeElement === $first[0]) {
                e.preventDefault();
                $last.trigger('focus');
            }
        } else {
            // Tab: if on last focusable, wrap to first
            if (document.activeElement === $last[0]) {
                e.preventDefault();
                $first.trigger('focus');
            }
        }
    };

    $(document).on('keydown.focusTrap', _activeTrapHandler);
}

/**
 * Release the focus trap and restore focus to the trigger element.
 */
function releaseFocus() {
    $(document).off('keydown.focusTrap');
    _activeTrapHandler = null;

    if (_triggerElement && typeof _triggerElement.focus === 'function') {
        _triggerElement.focus();
        _triggerElement = null;
    }
}

export { bindEscapeClose, trapFocus, releaseFocus };
